/* eslint-disable max-lines */
import * as React from 'react';
import { ModalWindow, Button, Tags, Spinner } from '@cian/ui-kit';

import { ISearchCatalogItem } from '../../../types/geo';
import {
  MOSCOW_MO,
  SPB_LO,
  convertToViewModel,
  separateByColumns,
  SeparatorProps,
  IRegion,
  IRegionsViewModel,
  Regions,
} from './util';

import {
  DEFAULT_REGION_CITIES_MAP,
  Regions as RegionsEnum,
  DEFAULT_REGIONS_MAP as DEFAULT_REGIONS_MAP_TEMPLATES,
} from '../../../interfaces/villages';
import { ICity, IGeo } from '../../../store/geo';
import { DC_REGIONS_MAP } from '../../../utils/geo';
import { RealtyRegions } from '../../../utils/regions_map';
import { isArrayWithItems } from '../../../utils';
import { LocationBreadcrumbs } from '../../filters/components/LocationBreadcrumbs';
import { LocationModalMobile } from '../../mobile/filters/components/LocationModalMobile';
import { LocationSearch } from '../../filters/components/LocationSearch';

import * as styles from './location_switcher.css';

export const escapeKeyCode = 27;

const BC_NAME_MAP: { [key: number]: string } = {
  [RealtyRegions.Moscow]: MOSCOW_MO,
  [RealtyRegions.StPetersburg]: SPB_LO,
};

const DEFAULT_REGIONS_MAP: { [key: number]: string } = {
  ...BC_NAME_MAP,
  [RealtyRegions.MoscowArea]: MOSCOW_MO,
  [RealtyRegions.StPetersburgArea]: SPB_LO,
};

export interface ILocationSwitcherProps {
  isPopupOpen: boolean;

  onLocationChange(location: IRegion, isDc?: boolean): void;
  onCloseButtonClick(): void;
  onRegionSelect(location: IRegion): void;
  fetchRegionsCities(text: string): void;

  expandedFavoritesRegions?: IRegion[];

  popupColumnsCount?: number;

  isMobileVersion?: boolean;

  geo: IGeo;

  filtersCleared?: boolean;
}

export interface ILocationSwitcherState {
  search?: string;
  selectedRegion?: IRegion;
  selectedCity?: ICity;
  regionsViewModel?: IRegionsViewModel;
  valuesAfterOpen: {
    search?: string;
    selectedRegion?: IRegion;
    selectedCity?: ICity;
    regionsViewModel?: IRegionsViewModel;
  };
}

interface IBreadcrumb {
  regionId: number;
  displayName: string;
  isSelected: boolean;
}

export class LocationSwitcher extends React.PureComponent<ILocationSwitcherProps, ILocationSwitcherState> {
  private firstOpen = true;
  private selectedRegionAfterSave: IRegion | undefined;

  public constructor(props: ILocationSwitcherProps) {
    super(props);

    this.state = this.getDefaultState(props);
  }

  private getDefaultState = (props: ILocationSwitcherProps) => {
    const regionsViewModel = convertToViewModel(props.geo.regions, false);

    if (props.expandedFavoritesRegions && props.expandedFavoritesRegions.length) {
      regionsViewModel.favorites = props.expandedFavoritesRegions.concat(regionsViewModel.favorites || []);
    }

    return {
      selectedCity:
        props.geo.selectedCity ||
        (props.geo.selectedRegion ? this.getSpecialCityParent(props.geo.selectedRegion) : undefined),
      selectedRegion: props.geo.selectedRegion,
      regionsViewModel,
      search: '',
      valuesAfterOpen: {},
    };
  };

  public UNSAFE_componentWillReceiveProps(nextProps: ILocationSwitcherProps) {
    const { selectedRegion } = nextProps.geo;
    const regionId = selectedRegion ? selectedRegion.mainTownId || selectedRegion.parentId || -1 : -1;
    const region = DEFAULT_REGIONS_MAP_TEMPLATES[regionId] || selectedRegion;

    if (nextProps.filtersCleared) {
      this.setState(this.getDefaultState(nextProps));
      this.selectedRegionAfterSave = undefined;
    }
    if (nextProps.isPopupOpen && this.firstOpen) {
      const valuesAfterOpen = { ...this.getDefaultState(nextProps) };
      const regionsViewModel =
        this.state.regionsViewModel && isArrayWithItems(this.state.regionsViewModel.alphabet)
          ? this.state.regionsViewModel
          : convertToViewModel(nextProps.geo.regions, false);

      this.setState({ valuesAfterOpen, selectedRegion: this.selectedRegionAfterSave || region, regionsViewModel });
      this.firstOpen = false;
    }
  }

  public render() {
    const { isMobileVersion } = this.props;
    const { selectedRegion } = this.state;
    let { regionsViewModel } = this.state;

    if (!regionsViewModel) {
      regionsViewModel = {
        alphabet: [] as Regions[],
        favorites: [] as IRegion[],
      };
    }

    if (selectedRegion) {
      regionsViewModel = convertToViewModel(this.props.geo.cities || [], true);
    }

    if (!regionsViewModel.alphabet) {
      regionsViewModel.alphabet = [] as Regions[];
    }

    if (!regionsViewModel.favorites) {
      regionsViewModel.favorites = [] as IRegion[];
    }

    const regions = separateByColumns(regionsViewModel.alphabet, new SeparatorProps(this.props.popupColumnsCount));
    const favorites = regionsViewModel.favorites.map((region: IRegion) => this.mapRegion(region as ICity));
    const alphabet = this.renderAlphabet(regions);

    return (
      <div>
        {isMobileVersion ? (
          <LocationModalMobile
            open={this.props.isPopupOpen}
            geo={this.props.geo}
            onClose={this.close}
            onTextChange={this.props.fetchRegionsCities}
            onItemSelect={this.handleItemSelectMobile}
          />
        ) : (
          <ModalWindow
            fixed
            width={1200}
            open={this.props.isPopupOpen}
            onClose={this.close}
            header={
              <>
                <LocationSearch
                  geo={this.props.geo}
                  onTextChange={this.props.fetchRegionsCities}
                  onItemSelect={this.handleLocationSwitcherInputSelect}
                />
                <div className={styles['breadcrumbs']}>
                  <LocationBreadcrumbs list={this.breadcrumbs} onItemClick={this.handleBreadcrumbClick} />
                </div>
              </>
            }
            footer={
              <div className={styles['footer']}>
                <Button
                  size="XS"
                  onClick={this.onLocationChange}
                  disabled={!this.state.selectedRegion && !this.state.selectedCity}
                >
                  Сохранить
                </Button>
              </div>
            }
          >
            <>
              <div className={styles['content']}>
                {!!favorites.length && <div className={styles['favorites']}>{favorites}</div>}
                <div className={styles['grid']}>{alphabet}</div>
              </div>
              {this.renderPreloader()}
            </>
          </ModalWindow>
        )}
      </div>
    );
  }

  private renderPreloader = () => {
    const { geoPopupFetching } = this.props.geo;

    return geoPopupFetching ? (
      <div className={styles['preloader']}>
        <Spinner size={48} />
      </div>
    ) : null;
  };

  private handleItemSelectMobile = (selectedRegion: ISearchCatalogItem) => {
    const regionData =
      this.props.geo.regions.find(reg => reg.id === selectedRegion.id) ||
      this.props.geo.regions.find(reg => reg.id === selectedRegion.parentId);

    this.setState({
      selectedCity: selectedRegion,
      selectedRegion: regionData,
    });

    const location = regionData || selectedRegion;

    if (location) {
      this.props.fetchRegionsCities('');
      this.props.onLocationChange(location, !selectedRegion);
      this.selectedRegionAfterSave = regionData;
    }

    this.firstOpen = true;
  };

  private handleLocationSwitcherInputSelect = (selectedRegion: ISearchCatalogItem) => {
    const regionData =
      this.props.geo.regions.find(reg => reg.id === selectedRegion.id) ||
      this.props.geo.regions.find(reg => reg.id === selectedRegion.parentId);
    this.setState({
      selectedCity: selectedRegion,
      selectedRegion: regionData,
    });

    this.props.onRegionSelect(regionData as ICity);
  };

  private get breadcrumbs() {
    const breadcrumbs: IBreadcrumb[] = [
      {
        displayName: 'Россия',
        isSelected: false,
        regionId: 0,
      },
    ];
    const { selectedRegion, selectedCity } = this.state;

    if (selectedRegion) {
      breadcrumbs.push({
        displayName: BC_NAME_MAP[selectedRegion.id] || selectedRegion.displayName,
        isSelected: this.isItemSelected(selectedRegion.id),
        regionId: selectedRegion.id,
      });
    }
    if (selectedCity) {
      const defaultRegion = this.getSpecialCityParent(selectedCity as ICity, false);
      if (defaultRegion) {
        breadcrumbs.forEach(bc => {
          bc.isSelected = false;
        });
        breadcrumbs.push({
          displayName: defaultRegion.displayName,
          isSelected: true,
          regionId: defaultRegion.id,
        });
      }
    }

    return breadcrumbs;
  }

  private handleBreadcrumbClick = (type: 'link' | 'button', index: number) => {
    const { selectedRegion } = this.state;
    const shouldResetAll = (type === 'link' && !index) || (type === 'button' && index === 1);

    if (shouldResetAll) {
      this.setState({
        selectedCity: undefined,
        selectedRegion: undefined,
      });

      return;
    }

    this.setState({
      selectedCity: undefined,
      selectedRegion: index >= 1 ? selectedRegion : undefined,
    });

    if (selectedRegion) {
      this.props.onRegionSelect(selectedRegion);
    }
  };

  private getSpecialCityParent = (selectedCity: ICity, checkOnlyParents?: boolean): ICity | undefined => {
    return (
      DEFAULT_REGION_CITIES_MAP[RegionsEnum.Moscow].find(
        city => (!checkOnlyParents && city.id === selectedCity.id) || city.id === selectedCity.regionId,
      ) ||
      DEFAULT_REGION_CITIES_MAP[RegionsEnum.Petersburg].find(
        city => (!checkOnlyParents && city.id === selectedCity.id) || city.id === selectedCity.regionId,
      )
    );
  };

  private renderAlphabet(regions: [[string, IRegion[]][]]): JSX.Element[] {
    return regions.map((column: Regions[], colIndex) => {
      const columns = column.map((group: Regions, grIndex) => {
        const items = (group[1] as IRegion[]).map((region: IRegion) => {
          return (
            <div key={colIndex + grIndex + region.id} className={styles['item']}>
              {this.mapRegion(region as ICity)}
            </div>
          );
        });

        return (
          <div key={grIndex + colIndex} className={styles['group']}>
            <div className={styles['group-title']}>{group[0]}</div>
            <div className={styles['group-list']}>{items}</div>
          </div>
        );
      });

      return (
        <div key={colIndex} className={styles['column']}>
          {columns}
        </div>
      );
    });
  }

  private mapRegion(region: ICity) {
    const { displayName, id, regionId, parentId } = region;
    const isSelected = this.isItemSelected(id, true) && this.state.selectedCity;
    const isCity = (id in BC_NAME_MAP && parentId !== null) || !!parentId || !!regionId;

    return isSelected ? (
      <div className={styles['tag']}>
        <Tags
          options={[{ value: id.toString(), label: displayName }]}
          onClose={this.handleRegionClick(region, !isCity)}
        />
      </div>
    ) : (
      <button key={id} className={styles['link']} onClick={this.handleRegionClick(region, !isCity)}>
        {displayName}
      </button>
    );
  }

  private handleRegionClick = (region: ICity, isRegion: boolean) => () => {
    const { selectedRegion, selectedCity = { id: -1 } } = this.state;
    const newCity = region.id === selectedCity.id ? undefined : region;
    const defaultRegion = this.getDefaultSpecialRegion(region);
    const defaultCity = this.getSpecialCityParent(region);

    this.setState({
      selectedCity: defaultCity && defaultRegion && defaultCity.id === defaultRegion.id ? undefined : newCity,
      selectedRegion: defaultRegion || (selectedRegion ? selectedRegion : region),
    });
    if (isRegion) {
      this.props.onRegionSelect(region);
    }
  };

  private getDefaultSpecialRegion = (region: ICity): ICity | undefined => {
    if (this.state.selectedCity || this.state.selectedRegion) {
      return undefined;
    }

    return {
      ...region,
      displayName: DEFAULT_REGIONS_MAP[region.id] || region.displayName,
      id: DC_REGIONS_MAP[region.id] || region.id,
    };
  };

  private onLocationChange = () => {
    const { selectedRegion, selectedCity } = this.state;
    const location = selectedCity || selectedRegion;

    if (location) {
      this.props.onLocationChange(location, !selectedCity);
      this.selectedRegionAfterSave = selectedRegion;
    }
    this.firstOpen = true;
  };

  private close = (): void => {
    const { valuesAfterOpen } = this.state;

    this.props.onCloseButtonClick();
    this.firstOpen = true;
    this.setState({
      ...valuesAfterOpen,
      search: '',
    });

    if (!valuesAfterOpen.selectedRegion) {
      return;
    }

    if (
      valuesAfterOpen.selectedCity &&
      this.props.geo.currentRegion &&
      valuesAfterOpen.selectedCity.parentId === valuesAfterOpen.selectedRegion.id
    ) {
      this.props.onRegionSelect(this.props.geo.currentRegion);
    } else {
      this.props.onRegionSelect(valuesAfterOpen.selectedRegion);
    }
  };

  private isItemSelected = (regionId: number, shouldCheckCitiesFirst?: boolean) => {
    const { selectedRegion = { id: -1 }, selectedCity = { id: -1 } } = this.state;

    return shouldCheckCitiesFirst
      ? selectedCity.id === regionId
      : selectedCity.id === regionId || selectedRegion.id === regionId;
  };
}
